import { useEffect, useMemo, useRef, useState } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { SelectField } from '#app/components/form/select-field.tsx'
import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/regulatory-monitoring.ts'
import {
	createContactReferenceDefinition,
	sanitizeContactReferenceDraft,
} from '#app/routes/mocra+/_form+/components/contact-references/definition.ts'
import { ContactOverlay } from '#app/routes/mocra+/_form+/components/contacts/overlay.tsx'
import {
	createEmptyContactDraft,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import { monitoringFeedDefinition } from '#app/routes/mocra+/_form+/components/monitoring-feeds/definition.ts'
import { MonitoringFeedEditOverlay } from '#app/routes/mocra+/_form+/components/monitoring-feeds/edit.tsx'
import {
	MonitoringFeedEntrySchema,
	type MonitoringFeedEntry,
} from '#app/routes/mocra+/_form+/components/monitoring-feeds/schema.ts'
import {
	createMonitoringFeedDraft,
	sanitizeMonitoringFeedDraft,
	isMonitoringFeedFrequencyValue,
	isMonitoringFeedSourceValue,
	monitoringFeedDraftToEntry,
	monitoringFeedEntryToDraft as ignoredMonitoringFeedEntryToDraft,
	type MonitoringFeedDraft,
} from '#app/routes/mocra+/_form+/components/monitoring-feeds/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'
import { type ContactInput } from '#app/routes/mocra+/_form+/contact-directory.ts'
import {
	normalizeContactReference,
	resolveContactDisplay,
	ContactReferenceSchema,
	type ContactReference,
} from '#app/routes/mocra+/_form+/contacts/reference.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'
import {
	useEntityCollection,
	type EntityCollectionResult,
} from '#app/routes/mocra+/_form+/hooks/use-entity-collection.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import {
	IMPLEMENTATION_TRACKING_OPTIONS,
	IMPLEMENTATION_WINDOW_OPTIONS,
	REGULATORY_FEED_FREQUENCY_OPTIONS as ignoredREGULATORY_FEED_FREQUENCY_OPTIONS,
	REGULATORY_FEED_OPTIONS as ignoredREGULATORY_FEED_OPTIONS,
	REGULATORY_REVIEW_CADENCE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { ChangeControlSection } from '#app/routes/mocra+/_form+/regulatory-monitoring/change-control-section.tsx'
import { MonitoringFeedsSection } from '#app/routes/mocra+/_form+/regulatory-monitoring/monitoring-feeds-section.tsx'
import { MonitoringOwnersSection } from '#app/routes/mocra+/_form+/regulatory-monitoring/monitoring-owners-section.tsx'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Regulatory Monitoring' },
]

const SECTION_KEY = 'regulatoryMonitoring'
const STEP_ID = 'regulatory-monitoring'
const REGULATORY_MONITORING_RESOURCES = createRouteSectionResources({
	route: '/mocra/regulatory-monitoring',
})

const CHANGE_CONTROL_STAGE_CONFIG = [
	{
		key: 'triage',
		label: 'Review / triage',
		question: 'Who do we contact to triage new regulatory changes?',
		defaultName: 'Triage contact',
		defaultTitle: 'Change-control triage lead',
		directorySource: 'Change-control — triage',
	},
	{
		key: 'approval',
		label: 'Approval decision',
		question: 'Who makes the final approval decision?',
		defaultName: 'Approval contact',
		defaultTitle: 'Change-control approver',
		directorySource: 'Change-control — approval',
	},
	{
		key: 'implementation',
		label: 'Implementation coordination',
		question: 'Who coordinates implementation activities?',
		defaultName: 'Implementation contact',
		defaultTitle: 'Implementation lead',
		directorySource: 'Change-control — implementation',
	},
	{
		key: 'communications',
		label: 'Communications / training',
		question: 'Who handles communications and training updates?',
		defaultName: 'Communications contact',
		defaultTitle: 'Change communications lead',
		directorySource: 'Change-control — communications',
	},
	{
		key: 'verification',
		label: 'Verification / close-out',
		question: 'Who verifies completion and closes out the change?',
		defaultName: 'Verification contact',
		defaultTitle: 'Verification lead',
		directorySource: 'Change-control — verification',
	},
] as const

const MONITORING_OWNER_CONFIG = [
	{
		key: 'federal',
		label: 'Federal rulemaking',
		question: 'Who tracks federal rulemaking updates?',
		description:
			'Monitor FDA proposed rules, guidance documents, enforcement actions, and related federal notices.',
		defaultName: 'Federal monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — federal',
	},
	{
		key: 'state',
		label: 'State legislation',
		question: 'Who monitors state cosmetic legislation and agency notices?',
		description:
			'Track state bills, rulemakings, and attorney general updates that affect cosmetics and personal care.',
		defaultName: 'State monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — state',
	},
	{
		key: 'international',
		label: 'International developments',
		question: 'Who keeps watch on international regulatory developments?',
		description:
			'Follow EU, UK, Canadian, and other market changes that may influence global product compliance.',
		defaultName: 'International monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — international',
	},
	{
		key: 'retailer',
		label: 'Retailer requirements',
		question:
			'Who follows retailer compliance requirements and portal updates?',
		description:
			'Monitor Sephora, Ulta, Amazon, and other retail partners for policy updates, scorecard changes, and portal requests.',
		defaultName: 'Retail monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — retailer',
	},
	{
		key: 'technical',
		label: 'Technical standards',
		question: 'Who tracks technical standards and scientific findings?',
		description:
			'Watch ingredient safety data, PFAS/talc testing requirements, ASTM/ISO updates, and scientific advisories.',
		defaultName: 'Technical monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — technical',
	},
	{
		key: 'other',
		label: 'Other focus area',
		question: 'Who covers any additional regulatory monitoring focus?',
		description:
			'Assign coverage for trade associations, advocacy groups, or other niche focus areas not captured above.',
		defaultName: 'Monitoring contact',
		defaultTitle: 'Regulatory monitoring owner',
		directorySource: 'Regulatory monitoring — other',
	},
] as const

type ChangeControlStageConfig = (typeof CHANGE_CONTROL_STAGE_CONFIG)[number]
type MonitoringOwnerConfig = (typeof MONITORING_OWNER_CONFIG)[number]

export type ChangeControlStageKey = ChangeControlStageConfig['key']
export type MonitoringOwnerStageKey = MonitoringOwnerConfig['key']

const MONITORING_OWNER_DEFINITIONS = Object.fromEntries(
	MONITORING_OWNER_CONFIG.map((config) => [
		config.key,
		createContactReferenceDefinition({
			defaultTitle: config.defaultTitle,
		}),
	]),
) as Record<
	MonitoringOwnerStageKey,
	ReturnType<typeof createContactReferenceDefinition>
>

const CHANGE_CONTROL_DEFINITIONS = Object.fromEntries(
	CHANGE_CONTROL_STAGE_CONFIG.map((config) => [
		config.key,
		createContactReferenceDefinition({
			defaultTitle: config.defaultTitle,
		}),
	]),
) as Record<
	ChangeControlStageKey,
	ReturnType<typeof createContactReferenceDefinition>
>

const monitoringOwnerShape = {
	federal: z.array(ContactReferenceSchema).default([]),
	state: z.array(ContactReferenceSchema).default([]),
	international: z.array(ContactReferenceSchema).default([]),
	retailer: z.array(ContactReferenceSchema).default([]),
	technical: z.array(ContactReferenceSchema).default([]),
	other: z.array(ContactReferenceSchema).default([]),
} satisfies Record<MonitoringOwnerStageKey, z.ZodTypeAny>

const MonitoringOwnersSchema = z.object(monitoringOwnerShape)

const changeControlShape = {
	triage: z.array(ContactReferenceSchema).default([]),
	approval: z.array(ContactReferenceSchema).default([]),
	implementation: z.array(ContactReferenceSchema).default([]),
	communications: z.array(ContactReferenceSchema).default([]),
	verification: z.array(ContactReferenceSchema).default([]),
} satisfies Record<ChangeControlStageKey, z.ZodTypeAny>

const ChangeControlSchema = z.object(changeControlShape)

const RegulatoryMonitoringSchema = z.object({
	monitoringOwners: MonitoringOwnersSchema,
	monitoringFeeds: z.array(MonitoringFeedEntrySchema).default([]),
	changeControl: ChangeControlSchema,
	reviewCadence: z.enum(
		REGULATORY_REVIEW_CADENCE_OPTIONS.map((option) => option.value) as [
			(typeof REGULATORY_REVIEW_CADENCE_OPTIONS)[number]['value'],
			...string[],
		],
	),
	implementationWindow: z.enum(
		IMPLEMENTATION_WINDOW_OPTIONS.map((option) => option.value) as [
			(typeof IMPLEMENTATION_WINDOW_OPTIONS)[number]['value'],
			...string[],
		],
	),
	trackingTool: z.enum(
		IMPLEMENTATION_TRACKING_OPTIONS.map((option) => option.value) as [
			(typeof IMPLEMENTATION_TRACKING_OPTIONS)[number]['value'],
			...string[],
		],
	),
})

export type RegulatoryMonitoringFormValues = z.infer<
	typeof RegulatoryMonitoringSchema
>

type MonitoringOwnerValues = RegulatoryMonitoringFormValues['monitoringOwners']
type ChangeControlValues = RegulatoryMonitoringFormValues['changeControl']

const DEFAULT_VALUES: RegulatoryMonitoringFormValues = {
	monitoringOwners: createEmptyMonitoringOwners(),
	monitoringFeeds: [],
	changeControl: createEmptyChangeControl(),
	reviewCadence: REGULATORY_REVIEW_CADENCE_OPTIONS[0]?.value ?? 'monthly',
	implementationWindow:
		IMPLEMENTATION_WINDOW_OPTIONS[0]?.value ?? 'within_90_days',
	trackingTool:
		IMPLEMENTATION_TRACKING_OPTIONS[0]?.value ?? 'qms_change_control',
}

export default function RegulatoryMonitoring() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const form = useSectionForm({
		section: SECTION_KEY,
		schema: RegulatoryMonitoringSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const {
		values,
		setValues,
		setFieldValue,
		getFieldErrors,
		submit,
		status,
		hasStoredValue,
		clear,
	} = form

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeRegulatoryMonitoringDraft(values)
		if (normalized && !regulatoryMonitoringValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (
			!normalized &&
			!regulatoryMonitoringValuesEqual(DEFAULT_VALUES, values)
		) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	const {
		contacts: directoryContacts,
		candidates: directoryCandidates,
		mergeContacts,
		upsertContact,
	} = useContactDirectory()

	const directoryMap = useMemo(() => {
		const map = new Map<string, (typeof directoryContacts)[number]>()
		for (const entry of directoryContacts) {
			map.set(entry.id, entry)
		}
		return map
	}, [directoryContacts])

	const monitoringFeedsCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		MonitoringFeedEntry,
		MonitoringFeedDraft
	>({
		form,
		fieldPath: 'monitoringFeeds',
		definition: monitoringFeedDefinition,
		getFieldErrorPaths: getMonitoringFeedFieldErrorPaths,
	})
	const monitoringOwnerConfigMap = useMemo(() => {
		const map = new Map<MonitoringOwnerStageKey, MonitoringOwnerConfig>()
		for (const config of MONITORING_OWNER_CONFIG) {
			map.set(config.key, config)
		}
		return map
	}, [])
	const changeControlConfigMap = useMemo(() => {
		const map = new Map<ChangeControlStageKey, ChangeControlStageConfig>()
		for (const config of CHANGE_CONTROL_STAGE_CONFIG) {
			map.set(config.key, config)
		}
		return map
	}, [])

	const monitoringOwnerFederalCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.federal',
		definition: MONITORING_OWNER_DEFINITIONS.federal,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('federal', index),
	})

	const monitoringOwnerStateCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.state',
		definition: MONITORING_OWNER_DEFINITIONS.state,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('state', index),
	})

	const monitoringOwnerInternationalCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.international',
		definition: MONITORING_OWNER_DEFINITIONS.international,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('international', index),
	})

	const monitoringOwnerRetailerCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.retailer',
		definition: MONITORING_OWNER_DEFINITIONS.retailer,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('retailer', index),
	})

	const monitoringOwnerTechnicalCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.technical',
		definition: MONITORING_OWNER_DEFINITIONS.technical,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('technical', index),
	})

	const monitoringOwnerOtherCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'monitoringOwners.other',
		definition: MONITORING_OWNER_DEFINITIONS.other,
		getFieldErrorPaths: (index) =>
			getMonitoringOwnerFieldErrorPaths('other', index),
	})

	const changeControlTriageCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'changeControl.triage',
		definition: CHANGE_CONTROL_DEFINITIONS.triage,
		getFieldErrorPaths: (index) =>
			getChangeControlFieldErrorPaths('triage', index),
	})

	const changeControlApprovalCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'changeControl.approval',
		definition: CHANGE_CONTROL_DEFINITIONS.approval,
		getFieldErrorPaths: (index) =>
			getChangeControlFieldErrorPaths('approval', index),
	})

	const changeControlImplementationCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'changeControl.implementation',
		definition: CHANGE_CONTROL_DEFINITIONS.implementation,
		getFieldErrorPaths: (index) =>
			getChangeControlFieldErrorPaths('implementation', index),
	})

	const changeControlCommunicationsCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'changeControl.communications',
		definition: CHANGE_CONTROL_DEFINITIONS.communications,
		getFieldErrorPaths: (index) =>
			getChangeControlFieldErrorPaths('communications', index),
	})

	const changeControlVerificationCollection = useEntityCollection<
		RegulatoryMonitoringFormValues,
		ContactReference,
		ContactDraft
	>({
		form,
		fieldPath: 'changeControl.verification',
		definition: CHANGE_CONTROL_DEFINITIONS.verification,
		getFieldErrorPaths: (index) =>
			getChangeControlFieldErrorPaths('verification', index),
	})

	const monitoringOwnerCollectionByKey: Record<
		MonitoringOwnerStageKey,
		EntityCollectionResult<ContactReference, ContactDraft>
	> = {
		federal: monitoringOwnerFederalCollection,
		state: monitoringOwnerStateCollection,
		international: monitoringOwnerInternationalCollection,
		retailer: monitoringOwnerRetailerCollection,
		technical: monitoringOwnerTechnicalCollection,
		other: monitoringOwnerOtherCollection,
	}

	const changeControlCollectionByKey: Record<
		ChangeControlStageKey,
		EntityCollectionResult<ContactReference, ContactDraft>
	> = {
		triage: changeControlTriageCollection,
		approval: changeControlApprovalCollection,
		implementation: changeControlImplementationCollection,
		communications: changeControlCommunicationsCollection,
		verification: changeControlVerificationCollection,
	}

	const monitoringOwnerCollections = MONITORING_OWNER_CONFIG.map((config) => ({
		config,
		collection: monitoringOwnerCollectionByKey[config.key],
	}))

	const changeControlCollections = CHANGE_CONTROL_STAGE_CONFIG.map(
		(config) => ({
			config,
			collection: changeControlCollectionByKey[config.key],
		}),
	)

	const monitoringOwnerStages = useMemo(() => {
		return monitoringOwnerCollections.map(({ config, collection }) => {
			const displays = collection.items.map((item) =>
				resolveContactDisplay(item.entry, directoryMap, {
					defaultName: config.defaultName,
					defaultTitle: config.defaultTitle,
				}),
			)
			const contacts = collection.items.map((item, index) => ({
				display: displays[index] ?? {
					contactId: item.entry.contactId ?? '',
					name: config.defaultName,
					email: item.entry.fallbackEmail ?? '',
					title: config.defaultTitle,
					phone: item.entry.fallbackPhone ?? '',
				},
				errors: item.errors,
			}))
			const existingContacts = collection.items.map((item) =>
				collection.sanitizeDraft(item.draft),
			)
			const listErrors = getFieldErrors(`monitoringOwners.${config.key}`)
			return {
				config,
				contacts,
				existingContacts,
				listErrors,
			}
		})
	}, [directoryMap, getFieldErrors, monitoringOwnerCollections])

	const monitoringOwnerSignature = useMemo(
		() =>
			monitoringOwnerStages
				.map((stage) =>
					stage.existingContacts
						.map((contact) =>
							[contact.id ?? '', contact.email, contact.name].join('|'),
						)
						.join('::'),
				)
				.join(';;'),
		[monitoringOwnerStages],
	)
	const monitoringOwnerMergeSignatureRef = useRef<string | null>(null)

	const changeControlStages = useMemo(() => {
		return changeControlCollections.map(({ config, collection }) => {
			const displays = collection.items.map((item) =>
				resolveContactDisplay(item.entry, directoryMap, {
					defaultName: config.defaultName,
					defaultTitle: config.defaultTitle,
				}),
			)
			const contacts = collection.items.map((item, index) => ({
				display: displays[index] ?? {
					contactId: item.entry.contactId ?? '',
					name: config.defaultName,
					email: item.entry.fallbackEmail ?? '',
					title: config.defaultTitle,
					phone: item.entry.fallbackPhone ?? '',
				},
				errors: item.errors,
			}))
			const existingContacts = collection.items.map((item) =>
				collection.sanitizeDraft(item.draft),
			)
			const listErrors = getFieldErrors(`changeControl.${config.key}`)
			return {
				config,
				contacts,
				existingContacts,
				listErrors,
			}
		})
	}, [changeControlCollections, directoryMap, getFieldErrors])

	const changeControlSignature = useMemo(
		() =>
			changeControlStages
				.map((stage) =>
					stage.existingContacts
						.map((contact) =>
							[contact.id ?? '', contact.email, contact.name].join('|'),
						)
						.join('::'),
				)
				.join(';;'),
		[changeControlStages],
	)
	const changeControlMergeSignatureRef = useRef<string | null>(null)

	useEffect(() => {
		if (!monitoringOwnerSignature) {
			monitoringOwnerMergeSignatureRef.current = null
			return
		}
		if (monitoringOwnerMergeSignatureRef.current === monitoringOwnerSignature)
			return
		monitoringOwnerMergeSignatureRef.current = monitoringOwnerSignature
		for (const stage of monitoringOwnerStages) {
			if (stage.existingContacts.length === 0) continue
			const inputs: ContactInput[] = stage.existingContacts
				.map(
					(draft): ContactInput => ({
						id: draft.id,
						name: draft.name,
						email: draft.email,
						title: draft.title,
						phone: draft.phone,
					}),
				)
				.filter((input) =>
					Boolean(input.id && input.email && input.email.length > 0),
				)
			if (inputs.length === 0) continue
			mergeContacts(inputs, {
				source: stage.config.directorySource,
			})
		}
	}, [mergeContacts, monitoringOwnerSignature, monitoringOwnerStages])

	useEffect(() => {
		if (!changeControlSignature) {
			changeControlMergeSignatureRef.current = null
			return
		}
		if (changeControlMergeSignatureRef.current === changeControlSignature)
			return
		changeControlMergeSignatureRef.current = changeControlSignature
		for (const stage of changeControlStages) {
			if (stage.existingContacts.length === 0) continue
			const inputs: ContactInput[] = stage.existingContacts
				.map(
					(draft): ContactInput => ({
						id: draft.id,
						name: draft.name,
						email: draft.email,
						title: draft.title,
						phone: draft.phone,
					}),
				)
				.filter((input) =>
					Boolean(input.id && input.email && input.email.length > 0),
				)
			if (inputs.length === 0) continue
			mergeContacts(inputs, {
				source: stage.config.directorySource,
			})
		}
	}, [changeControlSignature, changeControlStages, mergeContacts])

	const monitoringFeedItems = monitoringFeedsCollection.items.map((item) => ({
		draft: item.draft,
		errors: item.errors,
	}))

	const [editingMonitoringOwner, setEditingMonitoringOwner] = useState<{
		stage: MonitoringOwnerStageKey
		index: number
	} | null>(null)
	const [editingChangeControl, setEditingChangeControl] = useState<{
		stage: ChangeControlStageKey
		index: number
	} | null>(null)
	const [editingFeedIndex, setEditingFeedIndex] = useState<number | null>(null)

	const monitoringOwnerEditingStage =
		editingMonitoringOwner === null
			? null
			: (monitoringOwnerCollections.find(
					(stage) => stage.config.key === editingMonitoringOwner.stage,
				) ?? null)

	const monitoringOwnerEditingDraft =
		monitoringOwnerEditingStage && editingMonitoringOwner
			? monitoringOwnerEditingStage.collection.sanitizeDraft(
					monitoringOwnerEditingStage.collection.items[
						editingMonitoringOwner.index
					]?.draft ?? monitoringOwnerEditingStage.collection.createDraft(),
				)
			: createEmptyContactDraft()

	const changeControlEditingStage =
		editingChangeControl === null
			? null
			: (changeControlCollections.find(
					(stage) => stage.config.key === editingChangeControl.stage,
				) ?? null)

	const changeControlEditingDraft =
		changeControlEditingStage && editingChangeControl
			? changeControlEditingStage.collection.sanitizeDraft(
					changeControlEditingStage.collection.items[editingChangeControl.index]
						?.draft ?? changeControlEditingStage.collection.createDraft(),
				)
			: createEmptyContactDraft()

	const changeControlEditingExcludedEmails =
		changeControlEditingStage && editingChangeControl
			? changeControlEditingStage.collection.items
					.filter((_, idx) => idx !== editingChangeControl.index)
					.map((item) =>
						changeControlEditingStage.collection
							.sanitizeDraft(item.draft)
							.email.trim(),
					)
					.filter((email) => email.length > 0)
			: []

	const monitoringOwnerEditingExcludedEmails =
		monitoringOwnerEditingStage && editingMonitoringOwner
			? monitoringOwnerEditingStage.collection.items
					.filter((_, idx) => idx !== editingMonitoringOwner.index)
					.map((item) =>
						monitoringOwnerEditingStage.collection
							.sanitizeDraft(item.draft)
							.email.trim(),
					)
					.filter((email) => email.length > 0)
			: []

	const editingFeedDraft =
		editingFeedIndex !== null &&
		monitoringFeedsCollection.items[editingFeedIndex]
			? monitoringFeedsCollection.items[editingFeedIndex].draft
			: monitoringFeedsCollection.createDraft()

	const handleAddMonitoringOwnerContact = (
		stage: MonitoringOwnerStageKey,
		draft: ContactDraft,
	) => {
		const config = monitoringOwnerConfigMap.get(stage)
		const stageEntry = monitoringOwnerCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!config || !stageEntry) return
		const sanitized = sanitizeContactReferenceDraft(draft, config.defaultTitle)
		const contactId = sanitized.id?.trim()
		if (!contactId) return
		if (
			stageEntry.collection.entries.some((ref) => ref.contactId === contactId)
		)
			return
		stageEntry.collection.append(sanitized)
	}

	const handleRemoveMonitoringOwnerContact = (
		stage: MonitoringOwnerStageKey,
		index: number,
	) => {
		const stageEntry = monitoringOwnerCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!stageEntry) return
		stageEntry.collection.remove(index)
		if (
			editingMonitoringOwner &&
			editingMonitoringOwner.stage === stage &&
			editingMonitoringOwner.index === index
		) {
			setEditingMonitoringOwner(null)
		} else if (
			editingMonitoringOwner &&
			editingMonitoringOwner.stage === stage &&
			editingMonitoringOwner.index > index
		) {
			setEditingMonitoringOwner({
				stage,
				index: editingMonitoringOwner.index - 1,
			})
		}
	}

	const handleAssignMonitoringOwnerContact = (draft: ContactDraft) => {
		if (!editingMonitoringOwner) return
		const { stage, index } = editingMonitoringOwner
		const config = monitoringOwnerConfigMap.get(stage)
		const stageEntry = monitoringOwnerCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!config || !stageEntry) return
		const directoryEntry = upsertContact(draft, {
			source: config.directorySource,
		})
		const resolvedDraft = sanitizeContactReferenceDraft(
			{
				id: directoryEntry?.id ?? draft.id,
				title: directoryEntry?.title ?? draft.title,
				name: directoryEntry?.name ?? draft.name,
				email: directoryEntry?.email ?? draft.email,
				phone: directoryEntry?.phone ?? draft.phone,
			},
			config.defaultTitle,
		)
		if (!resolvedDraft.id) return
		stageEntry.collection.replace(index, resolvedDraft)
		setEditingMonitoringOwner(null)
	}

	const handleAddChangeControlContact = (
		stage: ChangeControlStageKey,
		draft: ContactDraft,
	) => {
		const config = changeControlConfigMap.get(stage)
		const stageEntry = changeControlCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!config || !stageEntry) return
		const sanitized = sanitizeContactReferenceDraft(draft, config.defaultTitle)
		const contactId = sanitized.id?.trim()
		if (!contactId) return
		if (
			stageEntry.collection.entries.some((ref) => ref.contactId === contactId)
		)
			return
		stageEntry.collection.append(sanitized)
	}

	const handleRemoveChangeControlContact = (
		stage: ChangeControlStageKey,
		index: number,
	) => {
		const stageEntry = changeControlCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!stageEntry) return
		stageEntry.collection.remove(index)
		if (
			editingChangeControl &&
			editingChangeControl.stage === stage &&
			editingChangeControl.index === index
		) {
			setEditingChangeControl(null)
		} else if (
			editingChangeControl &&
			editingChangeControl.stage === stage &&
			editingChangeControl.index > index
		) {
			setEditingChangeControl({
				stage,
				index: editingChangeControl.index - 1,
			})
		}
	}

	const handleAssignChangeControlContact = (draft: ContactDraft) => {
		if (!editingChangeControl) return
		const { stage, index } = editingChangeControl
		const config = changeControlConfigMap.get(stage)
		const stageEntry = changeControlCollections.find(
			(entry) => entry.config.key === stage,
		)
		if (!config || !stageEntry) return
		const directoryEntry = upsertContact(draft, {
			source: config.directorySource,
		})
		const resolvedDraft = sanitizeContactReferenceDraft(
			{
				id: directoryEntry?.id ?? draft.id,
				title: directoryEntry?.title ?? draft.title,
				name: directoryEntry?.name ?? draft.name,
				email: directoryEntry?.email ?? draft.email,
				phone: directoryEntry?.phone ?? draft.phone,
			},
			config.defaultTitle,
		)
		if (!resolvedDraft.id) return
		stageEntry.collection.replace(index, resolvedDraft)
		setEditingChangeControl(null)
	}

	const handleAddFeed = (draft: MonitoringFeedDraft) => {
		monitoringFeedsCollection.append(draft)
	}

	const handleEditFeed = (draft: MonitoringFeedDraft) => {
		if (editingFeedIndex === null) return
		monitoringFeedsCollection.replace(editingFeedIndex, draft)
		setEditingFeedIndex(null)
	}

	const handleRemoveFeed = (index: number) => {
		monitoringFeedsCollection.remove(index)
		if (editingFeedIndex === index) {
			setEditingFeedIndex(null)
		}
	}

	const handleReviewCadenceChange = (
		value: RegulatoryMonitoringFormValues['reviewCadence'],
	) => {
		setFieldValue('reviewCadence', value)
	}

	const handleImplementationWindowChange = (
		value: RegulatoryMonitoringFormValues['implementationWindow'],
	) => {
		setFieldValue('implementationWindow', value)
	}

	const handleTrackingToolChange = (
		value: RegulatoryMonitoringFormValues['trackingTool'],
	) => {
		setFieldValue('trackingTool', value)
	}

	return (
		<Page
			title="Regulatory Monitoring"
			description="Document who tracks rulemaking, the feeds you watch, and how change-control approvals move from insight to implementation."
			className="space-y-8"
		>
			<Form
				method="post"
				className="space-y-8"
				onSubmit={(event) => {
					event.preventDefault()
					void submit()
				}}
			>
				<Divider />

				<MonitoringFeedsSection
					resources={REGULATORY_MONITORING_RESOURCES}
					feeds={monitoringFeedItems}
					onAdd={handleAddFeed}
					onEdit={(index) => setEditingFeedIndex(index)}
					onRemove={handleRemoveFeed}
				/>
				<MonitoringFeedEditOverlay
					open={editingFeedIndex !== null}
					onOpenChange={(open) => {
						if (!open) setEditingFeedIndex(null)
					}}
					onSubmit={handleEditFeed}
					initialValue={editingFeedDraft}
					onClosed={() => setEditingFeedIndex(null)}
				/>

				<Divider />

				<MonitoringOwnersSection
					resources={REGULATORY_MONITORING_RESOURCES}
					stages={monitoringOwnerStages.map((stage) => ({
						key: stage.config.key,
						question: stage.config.question,
						description: stage.config.description,
						defaultName: stage.config.defaultName,
						defaultTitle: stage.config.defaultTitle,
						directorySource: stage.config.directorySource,
						contacts: stage.contacts,
						existingContacts: stage.existingContacts,
						listErrors: stage.listErrors,
					}))}
					onAddContact={handleAddMonitoringOwnerContact}
					onEditContact={(stage, index) =>
						setEditingMonitoringOwner({ stage, index })
					}
					onRemoveContact={handleRemoveMonitoringOwnerContact}
				/>
				<ContactOverlay
					mode="edit"
					open={editingMonitoringOwner !== null}
					onOpenChange={(open) => {
						if (!open) setEditingMonitoringOwner(null)
					}}
					onSubmit={handleAssignMonitoringOwnerContact}
					initialValue={monitoringOwnerEditingDraft}
					candidates={directoryCandidates}
					excludedEmails={monitoringOwnerEditingExcludedEmails}
					onClosed={() => setEditingMonitoringOwner(null)}
					onCancel={() => setEditingMonitoringOwner(null)}
					submitLabel="Save"
				/>

				<Divider />

				<ChangeControlSection
					resources={REGULATORY_MONITORING_RESOURCES}
					stages={changeControlStages.map((stage) => ({
						key: stage.config.key,
						label: stage.config.label,
						question: stage.config.question,
						defaultName: stage.config.defaultName,
						defaultTitle: stage.config.defaultTitle,
						directorySource: stage.config.directorySource,
						contacts: stage.contacts,
						existingContacts: stage.existingContacts,
						listErrors: stage.listErrors,
					}))}
					onAddContact={handleAddChangeControlContact}
					onEditContact={(stage, index) =>
						setEditingChangeControl({ stage, index })
					}
					onRemoveContact={handleRemoveChangeControlContact}
				/>
				<ContactOverlay
					mode="edit"
					open={editingChangeControl !== null}
					onOpenChange={(open) => {
						if (!open) setEditingChangeControl(null)
					}}
					onSubmit={handleAssignChangeControlContact}
					initialValue={changeControlEditingDraft}
					candidates={directoryCandidates}
					excludedEmails={changeControlEditingExcludedEmails}
					onClosed={() => setEditingChangeControl(null)}
					onCancel={() => setEditingChangeControl(null)}
					submitLabel="Save"
				/>

				<Divider />

				<Section
					title="Implementation approach"
					description="Show how often you review updates, how fast changes move, and where you track remediation."
					mocraSections={REGULATORY_MONITORING_RESOURCES.mocraSections}
					templates={REGULATORY_MONITORING_RESOURCES.templates}
					childrenClassName="space-y-4"
				>
					<SelectField
						labelProps={{
							children: 'How often do you review regulatory changes as a team?',
						}}
						selectProps={{
							value: values.reviewCadence,
							onChange: (event) =>
								handleReviewCadenceChange(event.target.value),
						}}
						options={REGULATORY_REVIEW_CADENCE_OPTIONS}
						errors={getFieldErrors('reviewCadence')}
					/>
					<SelectField
						labelProps={{
							children: 'How quickly do you implement required changes?',
						}}
						selectProps={{
							value: values.implementationWindow,
							onChange: (event) =>
								handleImplementationWindowChange(event.target.value),
						}}
						options={IMPLEMENTATION_WINDOW_OPTIONS}
						errors={getFieldErrors('implementationWindow')}
					/>
					<SelectField
						labelProps={{
							children: 'Where do you track implementation progress?',
						}}
						selectProps={{
							value: values.trackingTool,
							onChange: (event) => handleTrackingToolChange(event.target.value),
						}}
						options={IMPLEMENTATION_TRACKING_OPTIONS}
						errors={getFieldErrors('trackingTool')}
					/>
				</Section>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}
				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

function getMonitoringFeedFieldErrorPaths(index: number): readonly string[] {
	const prefix = `monitoringFeeds.${index}`
	return [prefix, `${prefix}.source`, `${prefix}.frequency`, `${prefix}.detail`]
}

function getMonitoringOwnerFieldErrorPaths(
	stage: MonitoringOwnerStageKey,
	index: number,
): readonly string[] {
	const prefix = `monitoringOwners.${stage}.${index}`
	return [prefix, `${prefix}.contactId`]
}

function getChangeControlFieldErrorPaths(
	stage: ChangeControlStageKey,
	index: number,
): readonly string[] {
	const prefix = `changeControl.${stage}.${index}`
	return [prefix, `${prefix}.contactId`]
}

function createEmptyMonitoringOwners(): MonitoringOwnerValues {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const base: MonitoringOwnerValues = {} as MonitoringOwnerValues
	for (const config of MONITORING_OWNER_CONFIG) {
		base[config.key] = []
	}
	return base
}

function createEmptyChangeControl(): ChangeControlValues {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const base: ChangeControlValues = {} as ChangeControlValues
	for (const config of CHANGE_CONTROL_STAGE_CONFIG) {
		base[config.key] = []
	}
	return base
}

function ensureMonitoringOwners(
	value: RegulatoryMonitoringFormValues['monitoringOwners'] | undefined,
): MonitoringOwnerValues {
	const base = createEmptyMonitoringOwners()
	if (!value || typeof value !== 'object') return base
	for (const config of MONITORING_OWNER_CONFIG) {
		const raw = (value as Record<string, unknown>)[config.key]
		if (!Array.isArray(raw)) continue
		base[config.key] = raw
			.map((entry) => normalizeReference(entry))
			.filter(
				(reference): reference is ContactReference =>
					reference !== null && reference.contactId.length > 0,
			)
	}
	return base
}

function ensureChangeControl(
	value: RegulatoryMonitoringFormValues['changeControl'] | undefined,
): ChangeControlValues {
	const base = createEmptyChangeControl()
	if (!value || typeof value !== 'object') return base
	for (const config of CHANGE_CONTROL_STAGE_CONFIG) {
		const raw = (value as Record<string, unknown>)[config.key]
		if (!Array.isArray(raw)) continue
		base[config.key] = raw
			.map((entry) => normalizeReference(entry))
			.filter(
				(reference): reference is ContactReference =>
					reference !== null && reference.contactId.length > 0,
			)
	}
	return base
}

function normalizeReference(input: unknown): ContactReference | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const contactId =
		typeof record.contactId === 'string' ? record.contactId.trim() : ''
	if (contactId.length === 0) return null
	return normalizeContactReference({
		contactId,
		fallbackName:
			typeof record.fallbackName === 'string' ? record.fallbackName : undefined,
		fallbackEmail:
			typeof record.fallbackEmail === 'string'
				? record.fallbackEmail
				: undefined,
		fallbackTitle:
			typeof record.fallbackTitle === 'string'
				? record.fallbackTitle
				: undefined,
		fallbackPhone:
			typeof record.fallbackPhone === 'string'
				? record.fallbackPhone
				: undefined,
	})
}

function normalizeRegulatoryMonitoringDraft(
	value: unknown,
): RegulatoryMonitoringFormValues | null {
	if (!value || typeof value !== 'object') return null
	const record = value as Record<string, unknown>
	const monitoringOwners = ensureMonitoringOwners(
		record.monitoringOwners as MonitoringOwnerValues | undefined,
	)
	const changeControl = ensureChangeControl(
		record.changeControl as ChangeControlValues | undefined,
	)
	const feedsRaw = Array.isArray(record.monitoringFeeds)
		? record.monitoringFeeds
		: []
	const monitoringFeeds = feedsRaw
		.map((entry) => monitoringFeedEntryToDraftLike(entry))
		.filter((entry): entry is MonitoringFeedEntry => entry !== null)
	const reviewCadence =
		typeof record.reviewCadence === 'string' &&
		REGULATORY_REVIEW_CADENCE_OPTIONS.some(
			(option) => option.value === record.reviewCadence,
		)
			? record.reviewCadence
			: DEFAULT_VALUES.reviewCadence
	const implementationWindow =
		typeof record.implementationWindow === 'string' &&
		IMPLEMENTATION_WINDOW_OPTIONS.some(
			(option) => option.value === record.implementationWindow,
		)
			? record.implementationWindow
			: DEFAULT_VALUES.implementationWindow
	const trackingTool =
		typeof record.trackingTool === 'string' &&
		IMPLEMENTATION_TRACKING_OPTIONS.some(
			(option) => option.value === record.trackingTool,
		)
			? record.trackingTool
			: DEFAULT_VALUES.trackingTool
	const candidate: RegulatoryMonitoringFormValues = {
		monitoringOwners,
		monitoringFeeds,
		changeControl,
		reviewCadence,
		implementationWindow,
		trackingTool,
	}
	const parsed = RegulatoryMonitoringSchema.safeParse(candidate)
	return parsed.success ? parsed.data : null
}

function regulatoryMonitoringValuesEqual(
	left: RegulatoryMonitoringFormValues,
	right: RegulatoryMonitoringFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function monitoringFeedEntryToDraftLike(
	input: unknown,
): MonitoringFeedEntry | null {
	if (!input || typeof input !== 'object') return null
	const record = input as Record<string, unknown>
	const draft = sanitizeMonitoringFeedDraft(
		createMonitoringFeedDraft({
			source: isMonitoringFeedSourceValue(record.source)
				? record.source
				: undefined,
			frequency: isMonitoringFeedFrequencyValue(record.frequency)
				? record.frequency
				: undefined,
			detail: typeof record.detail === 'string' ? record.detail : '',
		}),
	)
	return monitoringFeedDraftToEntry(draft)
}
